import { z } from 'zod';
import { TIERS } from './tiers';
import { SegmentRulesSchema } from './segment-rules';

export const FlowTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('signup') }),
  z.object({ type: z.literal('profile_completed') }),
  z.object({ type: z.literal('canceled') }),
  z.object({ type: z.literal('payment_failed') }),
  z.object({ type: z.literal('trial_ending') }),
  z.object({ type: z.literal('approved') }),
  z.object({ type: z.literal('cancel_requested') }),
  z.object({
    type: z.literal('upgrade'),
    filter: z.object({ fromTier: z.array(z.enum(TIERS)).optional(), toTier: z.array(z.enum(TIERS)).optional() }).optional(),
  }),
  z.object({
    type: z.literal('downgrade'),
    filter: z.object({ fromTier: z.array(z.enum(TIERS)).optional(), toTier: z.array(z.enum(TIERS)).optional() }).optional(),
  }),
  z.object({ type: z.literal('segment_entered'), segmentId: z.string().min(1) }),
  z.object({ type: z.literal('segment_exited'), segmentId: z.string().min(1) }),
  z.object({ type: z.literal('tag_added'), tag: z.string().min(1) }),
  z.object({ type: z.literal('inactive_for'), days: z.union([z.literal(7), z.literal(14), z.literal(30)]) }),
]);
export type FlowTrigger = z.infer<typeof FlowTriggerSchema>;

/** "3d", "12h", "30m" */
export const DurationSchema = z.string().regex(/^\d+(m|h|d)$/);

const baseStep = {
  send_email: z.object({
    type: z.literal('send_email'),
    templateId: z.string().min(1),
    domain: z.enum(['marketing', 'transactional']),
    skipIfReceivedWithinDays: z.number().nonnegative().optional(),
  }),
  wait: z.object({
    type: z.literal('wait'),
    duration: DurationSchema,
    until: z
      .object({
        weekday: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).optional(),
        hour: z.number().min(0).max(23).optional(),
        tz: z.string().optional(),
      })
      .optional(),
  }),
  add_tag: z.object({ type: z.literal('add_tag'), tag: z.string().min(1) }),
  remove_tag: z.object({ type: z.literal('remove_tag'), tag: z.string().min(1) }),
  add_to_segment: z.object({ type: z.literal('add_to_segment'), segmentId: z.string().min(1) }),
  exit_if: z.object({ type: z.literal('exit_if'), rules: SegmentRulesSchema }),
  exit: z.object({ type: z.literal('exit') }),
};

export type FlowStep =
  | z.infer<typeof baseStep.send_email>
  | z.infer<typeof baseStep.wait>
  | z.infer<typeof baseStep.add_tag>
  | z.infer<typeof baseStep.remove_tag>
  | z.infer<typeof baseStep.add_to_segment>
  | z.infer<typeof baseStep.exit_if>
  | z.infer<typeof baseStep.exit>
  | { type: 'branch'; if: z.infer<typeof SegmentRulesSchema>; then: FlowStep[]; else?: FlowStep[] };

export const FlowStepSchema: z.ZodType<FlowStep> = z.lazy(() =>
  z.discriminatedUnion('type', [
    baseStep.send_email,
    baseStep.wait,
    baseStep.add_tag,
    baseStep.remove_tag,
    baseStep.add_to_segment,
    baseStep.exit_if,
    baseStep.exit,
    z.object({
      type: z.literal('branch'),
      if: SegmentRulesSchema,
      then: z.array(FlowStepSchema),
      else: z.array(FlowStepSchema).optional(),
    }),
  ]),
);

export const FlowDefinitionSchema = z.object({
  name: z.string().min(1),
  trigger: FlowTriggerSchema,
  steps: z.array(FlowStepSchema),
  reentry: z.enum(['never', 'after_exit', 'always']).default('never'),
});
export type FlowDefinition = z.infer<typeof FlowDefinitionSchema>;
